import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import moment from "moment";
// Every moment/locale import also SETS moment's global locale, so en-gb stays the LAST
// import (unchanged default until `languageChanged` below applies the real language).
import "moment/locale/sk";
import "moment/locale/cs";
import "moment/locale/de";
import "moment/locale/it";
import "moment/locale/pl";
import "moment/locale/en-gb";
import dayjs from "dayjs";
import "dayjs/locale/sk";
import "dayjs/locale/cs";
import "dayjs/locale/de";
import "dayjs/locale/it";
import "dayjs/locale/pl";
import { HOST_LANGUAGES } from "../shared/i18n/languages";

const isDevelopment = process.env.NODE_ENV === "development";

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",

    // Pinned explicitly (2026-08-19, TAJMAC-PILOT-FE3c). This is already i18next's
    // DEFAULT, so setting it changes nothing today — it is here so that deliberately
    // EMPTY translation values stay empty. `account:departmentPlaceholder` is now "" in
    // all three locales (the owner asked for no suggested department); if this option
    // were ever flipped to false, i18next would treat "" as missing and render the raw
    // key ("departmentPlaceholder") inside the input instead.
    returnEmptyString: true,
    debug: isDevelopment,

    interpolation: {
      escapeValue: false,
    },

    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // Cache-busting (2026-07-23, QR-kiosk review §5 "raw i18n keys" bug): the JS
      // bundle is content-hashed but these JSON URLs were stable, so browsers kept
      // serving STALE cached translations against new code — new keys rendered as
      // raw key names even though the deployed files were correct. Bump this stamp
      // whenever locale JSON content changes (any locale commit) to force refetch.
      queryStringParams: { v: "20261101d" },
    },

    detection: {
      order: ["localStorage", "navigator", "htmlTag"],

      lookupLocalStorage: "i18nextLng",

      caches: ["localStorage"],
    },

    // sk/en/cs + every extra language switched on for THIS hostname in
    // shared/i18n/languageAvailability.json (USER-LANG-IT-PL-1, D4). A language off for this
    // host is unknown to i18next: a stored or browser `it` resolves to fallbackLng here.
    supportedLngs: [...HOST_LANGUAGES],

    nonExplicitSupportedLngs: false,

    defaultNS: "common",
    ns: ["common", "account", "settings", "public", "layout", "myMachinery", "marketplace", "inventory", "servicing", "serviceOffers", "serviceBoard", "notifications", "partnerHub", "chatBot", "partnerships", "serviceTracking", "operations", "machineEvidence", "errors"],
    react: {
      useSuspense: true,
    },
  });

// <html lang> follows the UI language (screen readers; CSS `hyphens: auto` for German).
// public/index.html ships lang="en"; i18next emits `languageChanged` once detection resolves.
const syncHtmlLang = (lng: string | undefined) => {
  if (lng && typeof document !== "undefined") document.documentElement.lang = lng.split("-")[0];
};

moment.locale(i18n.language);
dayjs.locale(i18n.language);
syncHtmlLang(i18n.language);
i18n.on('languageChanged', (lng) => {
  moment.locale(lng);
  dayjs.locale(lng);
  syncHtmlLang(lng);
});

export default i18n; 